/**
 * @author Hiroki Moto
 * @description https://marvelapp.com/4g4b5dg/screen/58965672
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import { connect } from 'react-redux';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import isNil from 'ramda/src/isNil';
import values from 'ramda/src/values';
import Avatar from '@components/@core/Avatar';
import DatePicker from '@components/@core/DatePicker';
import EditDropdown from '@components/EditDropdown';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import Filter from '@components/@core/Filter';
import Icon from '@components/@core/Icon';
import ToggleColumnMenu from '@components/@core/ToggleColumnMenu';
import AppActions from '~/actions/app';
import CompanyActions from '~/actions/company';
import DevelopActions from '~/actions/develop';
import ManageActions from '~/actions/manage';
import { NewHireReportMenu, ReportMenu } from '~/services/config';
import { exportCsv } from '~/services/util-excel';

const PdfIcon = loadable(() => import('./NewHirePdf'), { ssr: false });
const moment = extendMoment(originalMoment);

class NewHireOrientation extends Component {
    state = {
        userId: this.props.userId,
        company: this.props.company,
        dateStart: moment().startOf('month').format('YYYY-MM-DD'),
        dateEnd: moment().endOf('month').format('YYYY-MM-DD'),
        column: 1,
        showPrint: false,
    };

    componentDidMount() {
        const { company, dateStart, dateEnd } = this.state;
        this.props.getOrientations({
            company_id: [company.id],
            date_start: dateStart,
            date_end: dateEnd,
        });
    }

    handleFilter = (type, e) => {
        const { company, dateStart, dateEnd } = this.state;

        if (isNil(e)) return;
        if (type === 'company') {
            if (e[0].id !== company.id) {
                this.props.getOrientations({
                    company_id: [e[0].id],
                    date_start: dateStart,
                    date_end: dateEnd,
                });
                this.setState({ company: e[0] });
            }
        } else if (type === 'employee') {
        }
    };

    handleChangeDate = (e) => {
        const { company } = this.state;
        const dateStart = moment(e.start).format('YYYY-MM-DD');
        const dateEnd = moment(e.end).format('YYYY-MM-DD');
        this.props.getOrientations({
            company_id: company.id,
            date_start: dateStart,
            date_end: dateEnd,
        });
        this.setState({ dateStart, dateEnd });
    };

    handleSelectMenu = (item, e) => {
        const { company, dateStart, dateEnd } = this.state;
        const { certification_object } = item.report;
        switch (e) {
            case 'detail view':
                const route = `/hcm/report-certifications/${certification_object.user_id}/${certification_object.id}/view`;
                this.props.getCertificationDetail(certification_object, route);
                break;
            case 'edit assignment':
                this.props.toggleModal({
                    type: 'Quick Edit',
                    data: {
                        before: {
                            template: certification_object,
                            type: 'certifications',
                            from: 'instance',
                            after: {
                                card: certification_object,
                                mode: 'reassign',
                                type: 'FETCHHIREORIENTATIONREPORT_REQUEST',
                                payload: {
                                    company_id: company.id,
                                    date_start: dateStart,
                                    date_end: dateEnd,
                                },
                            },
                        },
                    },
                    callBack: {
                        onDelete: () => this.handleDeleteProgram(certification_object),
                    },
                });
                break;
            case 'unassign':
                this.handleDeleteProgram(certification_object);
                break;
            case 'assign':
                this.props.toggleModal({
                    type: 'Assign Program',
                    data: {
                        before: {
                            modules: [],
                            disabled: ['careers', 'badges'],
                            assignees: [item.id],
                            companyId: company.id,
                            after: {
                                type: 'FETCHHIREORIENTATIONREPORT_REQUEST',
                                payload: {
                                    company_id: company.id,
                                    date_start: dateStart,
                                    date_end: dateEnd,
                                },
                            },
                        },
                        after: [],
                    },
                });
                break;
            default:
                break;
        }
    };

    handleDeleteProgram = (program) => {
        const { company, dateStart, dateEnd } = this.state;
        this.props.deleteCertification({
            event: 'delete',
            data: {
                program: { id: program.id },
            },
            after: {
                type: 'FETCHHIREORIENTATIONREPORT_REQUEST',
                payload: {
                    company_id: company.id,
                    date_start: dateStart,
                    date_end: dateEnd,
                },
            },
        });
    };

    handleVisible = (column) => {
        this.setState({ column });
    };

    handleExcelReport = () => {
        this.props.toggleModal({
            type: 'Confirm',
            data: {
                before: {
                    title: 'Confirm',
                    body: 'Would you like to download this reports data to excel?',
                },
            },
            callBack: {
                onYes: () => {
                    this.handleExcel();
                },
            },
        });
    };

    handleExcel = () => {
        const { newHireReport } = this.props;
        let excelData = [];
        values(newHireReport.individuals).map((item) => {
            const { name, report } = item;

            const { certification_object } = report;
            const courses = isNil(certification_object) ? null : certification_object.stats.courses;
            const habits = isNil(certification_object) ? null : certification_object.stats.habits;
            const quotas = isNil(certification_object) ? null : certification_object.stats.quotas;
            const created_at = isNil(certification_object) ? null : certification_object.created_at;
            const est_completion = isNil(certification_object) ? null : certification_object.estimated_completion;
            const completed_at = isNil(certification_object) ? null : certification_object.completed_at;
            const habitsComplete = isNil(habits) ? 0 : habits.day.complete + habits.week.complete + habits.month.complete;
            const habitsTotal = isNil(habits) ? 0 : habits.day.total + habits.week.total + habits.month.total;
            const habitsCompletion = isNil(habits) || habitsTotal === 0 ? 0 : Math.ceil((habitsComplete * 100) / habitsTotal);
            const scheduleData = {
                Employees: name,
                Courses: `${isNil(courses) ? `0/0` : `${courses.complete}/${courses.total}`} \n${isNil(courses) ? `0%` : `${courses.completion}%`}`,
                Habits: `${isNil(habits) ? `0/0` : `${habitsComplete}/${habitsTotal}`} \n${isNil(habits) ? `0%` : `${habitsCompletion}%`}`,
                Quotas: `${isNil(quotas) ? `0/0` : `${quotas.complete}/${quotas.total}`} \n${isNil(quotas) ? `0%` : `${quotas.completion}%`}`,
                Assigned: isNil(created_at) ? 'NA' : moment(created_at).format('MMM DD, YY'),
                'Est. Completion': isNil(est_completion) ? 'NA' : moment(est_completion).format('MMM DD, YY'),
                Completed: isNil(completed_at) ? 'NA' : moment(completed_at).format('MMM DD, YY'),
            };
            excelData.push(scheduleData);
        });

        const key = ['Employees', 'Courses', 'Habits', 'Quotas', 'Assigned', 'Est. Completion', 'Completed'];
        exportCsv(excelData, key, 'Reports-New-Hire Orientation', true);
    };

    handleReportMenu = (e) => {
        switch (e) {
            case 'export':
                this.handleExcelReport();
                break;
            case 'print':
                this.setState({ showPrint: true });
                break;
        }
    };

    render() {
        const { newHireReport, role } = this.props;
        const { dateStart, dateEnd, column } = this.state;
        const date = moment.range(dateStart, dateEnd);

        return (
            <ErrorBoundary className="mng-new-hire-orientation">
                <Filter onChange={this.handleFilter} />
                <div className="list">
                    <div className="d-h-center mb-0 mb-md-2">
                        <p className="dsl-b22 text-500 d-flex-1">New Hire Orientation</p>
                        <div className="mb-2 d-flex">
                            <DatePicker calendar="range" append="caret" format="MMM D" align="right" as="span" value={date} closeAfterSelect onSelect={this.handleChangeDate} />
                            <EditDropdown className="ml-3" dataCy="editDropdown" options={ReportMenu} onChange={this.handleReportMenu} />
                            {this.state.showPrint && <PdfIcon state={this.state} props={this.props} name="Print" />}
                        </div>
                    </div>
                    <ToggleColumnMenu column={column} onVisible={this.handleVisible} activeTab="newHire" className="d-md-none mb-2" total={2} />
                    <div className="list-item pb-3 desktop-screen">
                        <div className="d-flex-5 dsl-m12 text-400">Employees</div>
                        <div className="d-flex-2 dsl-m12 text-400 text-end">Courses</div>
                        <div className="d-flex-2 dsl-m12 text-400 text-end">Habits</div>
                        <div className="d-flex-2 dsl-m12 text-400 text-end">Goals</div>
                        <div className="d-flex-1 dsl-m12 d-none d-md-block" />
                        <div className="d-flex-3 dsl-m12 text-400 ms-0 text-center ssm-right">Assigned</div>
                        <div className="d-flex-3 dsl-m12 text-400 ms-0 text-center ssm-right">Est completion</div>
                        <div className="d-flex-3 dsl-m12 text-400 ms-0 text-center ssm-right">Completed</div>
                        <div className="d-flex-1 dsl-m12" />
                    </div>

                    {values(newHireReport.individuals).length === 0 ? (
                        <div className="d-flex-7 py-3">
                            <p className="dsl-b16 mb-0 text-center">You have no new hires in progress for orientation</p>
                        </div>
                    ) : (
                        values(newHireReport.individuals).map((item) => {
                            const { id, name, avatar, report } = item;
                            const { certification_object } = report;
                            const courses = isNil(certification_object) ? null : certification_object.stats.courses;
                            const habits = isNil(certification_object) ? null : certification_object.stats.habits;
                            const quotas = isNil(certification_object) ? null : certification_object.stats.quotas;
                            const created_at = isNil(certification_object) ? null : certification_object.created_at;
                            const est_completion = isNil(certification_object) ? null : certification_object.estimated_completion;
                            const completed_at = isNil(certification_object) ? null : certification_object.completed_at;
                            const habitsComplete = isNil(habits) ? 0 : habits.day.complete + habits.week.complete + habits.month.complete;
                            const habitsTotal = isNil(habits) ? 0 : habits.day.total + habits.week.total + habits.month.total;
                            const habitsCompletion = isNil(habits) || habitsTotal === 0 ? 0 : Math.ceil((habitsComplete * 100) / habitsTotal);

                            return (
                                <div className="list-item d-flex ssm-column ai-center py-4" key={id}>
                                    <div className="d-flex-5 w-100 d-flex ai-center text-400">
                                        <Avatar name={name} url={`${avatar}${Date.now()}`} />
                                        <div className="dsl-b14 ms-3 cursor-pointer text-400 fw-normal truncate">{name}</div>
                                        <div className="d-flex-1 mobile-screen">
                                            {isNil(certification_object) ? (
                                                <>{role < 3 && <EditDropdown options={['Assign']} onChange={(e) => this.handleSelectMenu(item, e)} />}</>
                                            ) : (
                                                <EditDropdown options={NewHireReportMenu[role]} onChange={(e) => this.handleSelectMenu(item, e)} />
                                            )}
                                        </div>
                                    </div>
                                    {isNil(certification_object) ? (
                                        <>
                                            <div className="d-flex-7">
                                                <p className="dsl-b14 mb-0 text-center">No New Hire program assigned</p>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="d-flex-2 pt-2 d-flex flex-sm-column ai-center w-100 ps-0 ssp-l-3">
                                                <span className="dsl-b14 w-30 text-400 mobile-screen text-capitalize">course</span>
                                                <p className="dsl-b14 mb-1 fw-normal text-end">{isNil(courses) ? `0/0` : `${courses.complete}/${courses.total}`}</p>
                                                <p className="dsl-b13 mb-1 ms-0 ssm-l-2 text-end">{isNil(courses) ? `0%` : `${courses.completion}%`}</p>
                                            </div>
                                            <div className="d-flex-2 d-flex flex-sm-column pt-2 ai-center w-100 ps-0 ssp-l-3">
                                                <span className="dsl-b14 w-30 text-400 mobile-screen text-capitalize">habit</span>
                                                <p className="dsl-b14 mb-1 fw-normal text-end">
                                                    {habitsComplete}/{habitsTotal}
                                                </p>
                                                <p className="dsl-b13 mb-1 ms-0 ssm-l-2 text-end">{habitsCompletion}%</p>
                                            </div>
                                            <div className="d-flex-2 d-flex flex-sm-column pt-2 ai-center w-100 ps-0 ssp-l-3">
                                                <span className="dsl-b14 w-30 text-400 mobile-screen text-capitalize">Goal</span>
                                                <p className="dsl-b14 mb-1 fw-normal text-end">{isNil(quotas) ? `0/0` : `${quotas.complete}/${quotas.total}`}</p>
                                                <p className="dsl-b13 mb-1 ms-0 ssm-l-2 text-end">{isNil(quotas) ? `0%` : `${quotas.completion}%`}</p>
                                            </div>
                                            <div className="d-flex-1 dsl-m12 d-flex flex-sm-column ai-center desktop-screen w-100" />
                                            <div className="d-flex-3 dsl-b14 d-flex fw-normal ms-0 text-center ssm-left pt-2 w-100 ps-0 ssp-l-3">
                                                <span className="dsl-b14 w-30 text-400 mobile-screen text-capitalize">assign</span>
                                                <span>{isNil(created_at) ? 'NA' : moment(created_at).format('MMM DD, YY')}</span>
                                            </div>
                                            <div className="d-flex-3 dsl-b14 fw-normal ms-0 text-center ssm-left pt-2 d-flex w-100 ps-0 ssp-l-3">
                                                <span className="dsl-b14 w-30 text-400 mobile-screen">Completion</span>
                                                <span>{isNil(est_completion) ? 'NA' : moment(est_completion).format('MMM DD, YY')}</span>
                                            </div>
                                            <div className="d-flex-3 dsl-b14 fw-normal d-flex ms-0 pt-2 text-center ssm-left w-100 ps-0 ssp-l-3">
                                                <span className="dsl-b14 w-30 text-400 mobile-screen">Est. Completion</span>
                                                <span>{isNil(completed_at) ? 'Incomplete' : moment(completed_at).format('MMM DD, YY')}</span>
                                            </div>
                                        </>
                                    )}
                                    <div className="d-flex-1 desktop-screen">
                                        {isNil(certification_object) ? (
                                            <>{role < 3 && <EditDropdown options={['Assign']} onChange={(e) => this.handleSelectMenu(item, e)} />}</>
                                        ) : (
                                            <EditDropdown options={NewHireReportMenu[role]} onChange={(e) => this.handleSelectMenu(item, e)} />
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </ErrorBoundary>
        );
    }
}

NewHireOrientation.propTypes = {
    userId: PropTypes.number,
    role: PropTypes.number,
    company: PropTypes.shape({
        id: PropTypes.number,
    }),
    newHireReport: PropTypes.shape({
        companies: PropTypes.any,
        individuals: PropTypes.any,
        totals: PropTypes.shape({
            certifications_assigned: PropTypes.number,
            certifications_authored: PropTypes.number,
            certifications_completed: PropTypes.number,
            new_hires: PropTypes.number,
        }),
    }),
    getOrientations: PropTypes.func,
    getCertificationDetail: PropTypes.func,
    deleteCertification: PropTypes.func,
};

NewHireOrientation.defaultProps = {
    userId: 0,
    role: 1,
    company: {
        id: 0,
    },
    newHireReport: {
        companies: {},
        individuals: {},
        totals: {
            certifications_assigned: 0,
            certifications_authored: 0,
            certifications_completed: 0,
            new_hires: 0,
        },
    },
    getOrientations: () => {},
    getCertificationDetail: () => {},
    deleteCertification: () => {},
};

const mapStateToProps = (state) => ({
    userId: state.app.id,
    company: state.app.company_info,
    newHireReport: state.manage.newHireReport,
});

const mapDispatchToProps = (dispatch) => ({
    getOrientations: (e) => dispatch(ManageActions.fetchhireorientationreportRequest(e)),
    getCertificationDetail: (e, route) => dispatch(DevelopActions.libraryprogramdetailRequest(e, 'certifications', route)),
    deleteCertification: (e) => dispatch(DevelopActions.programeventRequest(e)),
    toggleModal: (e) => dispatch(AppActions.modalRequest(e)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NewHireOrientation);
